import React, { useState } from "react";
import { X, Share2, Copy, Check, Loader2, Trash2, ExternalLink, Save } from "lucide-react";
import api from "../../lib/api";

/**
 * ShareModal — generate / revoke / copy a public share link for the current
 * cover project. Disabled until the project has been saved (because we need
 * the project id to attach the token to).
 */
const ShareModal = ({ open, onClose, projectId, initialToken, onSaveFirst, onTokenChange }) => {
  const [token, setToken] = useState(initialToken || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (open) {
      setToken(initialToken || "");
      setError("");
      setCopied(false);
    }
  }, [open, initialToken]);

  if (!open) return null;

  const shareUrl = token ? `${window.location.origin}/api/share/${token}` : "";

  const handleGenerate = async () => {
    if (!projectId) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/cover/${projectId}/share`);
      setToken(data.share_token);
      onTokenChange?.(data.share_token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate share link.");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!projectId) return;
    if (!window.confirm("Revoke the share link? Anyone visiting it will see a 'not found' page.")) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/cover/${projectId}/share`);
      setToken("");
      onTokenChange?.("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not revoke share link.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Fallback selection
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2C2A]/55 backdrop-blur-sm px-4"
      data-testid="share-modal"
    >
      <div className="w-full max-w-lg bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl">
        <div className="px-6 py-4 border-b border-[#D3D1C7] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Share2 className="w-5 h-5 text-[#534AB7]" strokeWidth={1.6} />
            <h2 className="text-xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
              Share preview
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
            aria-label="Close"
            data-testid="share-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#5F5E5A] leading-relaxed">
            Generate a public read-only link to your cover. Send it to beta readers,
            editors, or post it on social — they'll see your assembled full wrap
            without needing an account.
          </p>

          {!projectId && (
            <div className="rounded-[8px] border border-[#E4D6BA] bg-[#FAF5E8] px-3 py-3 text-xs text-[#7A5C0E] flex items-start gap-2" data-testid="share-needs-save">
              <Save className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div className="flex-1">
                Save your project first — you'll be able to share it once it has an id.
              </div>
              <button
                onClick={onSaveFirst}
                data-testid="share-save-now-btn"
                className="text-[#534AB7] underline-offset-2 hover:underline shrink-0"
              >
                Save now
              </button>
            </div>
          )}

          {projectId && !token && (
            <button
              onClick={handleGenerate}
              disabled={busy}
              data-testid="share-generate-btn"
              className="w-full px-4 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Generate public share link
            </button>
          )}

          {token && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-1">
                  Public preview URL
                </div>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    data-testid="share-url-input"
                    className="flex-1 px-3 py-2 rounded-[8px] bg-[#FAFAF9] border border-[#D3D1C7] text-xs font-mono text-[#2C2C2A] focus:outline-none focus:border-[#534AB7]"
                  />
                  <button
                    onClick={handleCopy}
                    data-testid="share-copy-btn"
                    className={`px-3 rounded-[8px] border text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                      copied
                        ? "border-[#1D9E75] text-[#1D9E75] bg-[#E8F6F0]"
                        : "border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8]"
                    }`}
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-open-btn"
                  className="text-xs text-[#534AB7] hover:underline inline-flex items-center gap-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open preview
                </a>
                <button
                  onClick={handleRevoke}
                  disabled={busy}
                  data-testid="share-revoke-btn"
                  className="text-xs text-[#5F5E5A] hover:text-[#D74343] inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Revoke link
                </button>
              </div>
              <p className="text-[11px] text-[#5F5E5A] leading-snug">
                Updates to your cover are reflected on the public link the next time
                you save the project.
              </p>
            </>
          )}

          {error && (
            <div className="text-xs text-[#D74343]" data-testid="share-error">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
